// covered-by: frontends/carrie/src/ask-v2/review/__tests__/awaitingReviewScope.test.ts
import { type AskPunttMessage } from "@mg/schemas/src/christo/askPuntt";

import { isReviewStartUserMessage } from "../rail/isReviewStartUserMessage";

const REVIEW_PREFLIGHT_ID = "ask-puntt.chat-atom.review-preflight";

function messageHasReviewPreflight(message: AskPunttMessage): boolean {
  return (message.components ?? []).some(
    (atom) => atom.componentId === REVIEW_PREFLIGHT_ID,
  );
}

const SCOPE_SETUP_NAMES = new Set([
  "artwork-review",
  "artwork_review",
  "artwork-input-classifier",
]);

function valueNamesScopeSetup(value: unknown): boolean {
  if (typeof value === "string") return SCOPE_SETUP_NAMES.has(value);
  if (Array.isArray(value)) return value.some(valueNamesScopeSetup);
  if (value != null && typeof value === "object") {
    return Object.values(value as Record<string, unknown>).some(
      valueNamesScopeSetup,
    );
  }
  return false;
}

function stepIsScopeSetup(
  step: NonNullable<AskPunttMessage["agentSteps"]>[number],
): boolean {
  if (step.type !== "tool_call" && step.type !== "tool_result") return false;
  if (step.tool === "list_expert_methods") return true;
  if (
    step.tool !== "load_capability" &&
    step.tool !== "run_subagent" &&
    step.tool !== "run_subagents"
  ) {
    return false;
  }
  // why: a substring match on "artwork-review" also matches
  // "artwork-review-full", which is the review itself, after Start.
  return step.type === "tool_call" && valueNamesScopeSetup(step.args);
}

function turnIsBuildingScope(messages: readonly AskPunttMessage[]): boolean {
  for (const message of messages) {
    if ((message.agentSteps ?? []).some(stepIsScopeSetup)) return true;
  }
  const lastUser = [...messages]
    .reverse()
    .find((message) => message.role === "user");
  return (lastUser?.attachments?.length ?? 0) > 0;
}

/**
 * The wait before ReviewPreflight's scope card, and not the review itself.
 *
 * A file the person just attached is how an artwork review starts, and the
 * card is the first thing that review draws. Once that card is in the thread,
 * or she has sent Start review, this is no longer that wait.
 */
export function isAwaitingReviewScope(
  messages: readonly AskPunttMessage[],
  waiting: boolean,
): boolean {
  if (!waiting) return false;
  let start = -1;
  messages.forEach((message, index) => {
    if (
      message.role === "user" &&
      isReviewStartUserMessage(message.content ?? "")
    ) {
      start = index;
    }
  });
  const since = start < 0 ? messages : messages.slice(start + 1);
  if (since.some(messageHasReviewPreflight)) return false;
  // why: load_capability id "artwork-review" is the review after Start, and that name is also scope setup, so an accepted Start with no later user turn is not another wait.
  if (start >= 0 && !since.some((message) => message.role === "user")) {
    return false;
  }
  return turnIsBuildingScope(since);
}
